import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { MatButtonModule } from '@angular/material/button'
import { MatSelectModule } from '@angular/material/select'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatTabsModule } from '@angular/material/tabs'
import { FormsModule } from '@angular/forms'
import { DecimalPipe } from '@angular/common'
import { ContractItemService } from '../../../core/services/contract/contract-item/contract-item.service'
import { GroupingSuggestionService } from '../../../core/services/grouping-suggestion/grouping-suggestion.service'
import { ContratoItemServicoAdicionalService } from '../../../core/services/contrato-item-servico-adicional/contrato-item-servico-adicional.service'
import { ServiceAdditionalService } from '../../../core/services/service/service-additional/service-additional.service'
import { StretchTypeService } from '../../../core/services/stretch/stretch-type/stretch-type.service'
import { PassageiroItemContratoService } from '../../../core/services/passageiro-item-contrato/passageiro-item-contrato.service'
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service'
import { DialogConfirmComponent } from '../../../shared/components/dialog-confirm/dialog-confirm.component'
import moment from 'moment'

@Component({
  selector: 'logistics-contract-item-edit',
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatTabsModule,
    FormsModule,
    DecimalPipe,
  ],
  templateUrl: './logistics-contract-item-edit.component.html',
  styleUrl: './logistics-contract-item-edit.component.scss',
})
export class LogisticsContractItemEditComponent implements OnInit {
  @Output() response = new EventEmitter<boolean>()

  // ── Estado geral ───────────────────────────────────────────────────────────
  loading = false
  saving = false
  item: any = null

  // ── Formulário de edição ───────────────────────────────────────────────────
  editForm = {
    tipoTrechoId: null as number | null,
    dataInicio: '',
    horaInicio: '',
    dataFinal: '',
    horaFinal: '',
    kmPrevisto: null as number | null,
    observacao: '',
  }
  stretchTypes: any[] = []

  // ── Passageiros ────────────────────────────────────────────────────────────
  removingAll = false
  removingPassageiroId: string | null = null

  showAddPassageiro = false
  disponiveis: any[] = []
  loadingDisponiveis = false
  addingPassageiroId: string | null = null

  // ── Serviços Adicionais ────────────────────────────────────────────────────
  servicosAdicionais: any[] = []
  loadingServicos = false
  valorTotalServicos: number | null = null
  catalogoServicos: any[] = []
  addingServico = false
  removingServicoId: string | null = null
  servicoForm = {
    servicoAdicionalId: null as number | null,
    dataServico: '',
    quantidade: 1,
    taxa: 0,
    valorUnitario: 0,
    valorTotal: 0,
    observacao: '',
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { id: string },
    private dialogRef: MatDialogRef<LogisticsContractItemEditComponent>,
    private dialog: MatDialog,
    private contractItemService: ContractItemService,
    private sugestaoService: GroupingSuggestionService,
    private contratoItemServicoAdicionalService: ContratoItemServicoAdicionalService,
    private serviceAdditionalService: ServiceAdditionalService,
    private stretchTypeService: StretchTypeService,
    private passageiroItemContratoService: PassageiroItemContratoService,
    private snackbar: SnackbarCustomService,
  ) {}

  ngOnInit() {
    this.loadItem()
    this.loadStretchTypes()
    this.loadCatalogo()
  }

  // ── Item principal ─────────────────────────────────────────────────────────

  loadItem() {
    this.loading = true
    this.contractItemService.getContractItembyId(this.data.id as any).subscribe({
      next: (res: any) => {
        this.item = res.data
        this.fillForm(res.data)
        this.loadServicos()
      },
      error: () => {
        this.snackbar.error('Não foi possível carregar o item.')
        this.loading = false
      },
      complete: () => { this.loading = false },
    })
  }

  private fillForm(item: any) {
    const toNum = (v: any) => v != null ? Number(v) : null
    this.editForm = {
      tipoTrechoId: toNum(item.tipoTrechoId),
      dataInicio: this.toDateInput(item.dataInicio),
      horaInicio: item.horaInicio?.substring(0, 5) ?? '',
      dataFinal: this.toDateInput(item.dataFinal),
      horaFinal: item.horaFinal?.substring(0, 5) ?? '',
      kmPrevisto: item.kmPrevisto ?? null,
      observacao: item.observacao ?? '',
    }
  }

  loadStretchTypes() {
    this.stretchTypeService.listStretchType().subscribe({
      next: (r: any) => { this.stretchTypes = r.data || [] },
      error: () => {},
    })
  }

  close() {
    this.dialogRef.close()
  }

  save() {
    if (!this.item) return
    this.saving = true

    const formatDate = (d: string) => d ? moment(d, 'YYYY-MM-DD').format('DD/MM/YYYY') : null
    const formatHora = (h: string) => {
      if (!h) return ''
      // Remove qualquer caractere não numérico e garante 4 dígitos (hhmm) antes de formatar
      const paddedTime = h.replace(/\D/g, '').padStart(4, '0')
      return `${paddedTime.substr(0, 2)}:${paddedTime.substr(2, 2)}:00`
    }

    const model = {
      id: this.item.id,
      tipoMotoristaId: this.item.tipoMotoristaId ?? null,
      tipoVeiculoId: this.item.tipoVeiculoId ?? null,
      origemId: this.item.origemId ?? 0,
      destinoId: this.item.destinoId ?? 0,
      servicoId: this.item.servicoId ?? null,
      tipoServicoId: this.item.tipoServicoId ?? null,
      tipoTrechoId: this.editForm.tipoTrechoId ?? null,
      dataInicio: formatDate(this.editForm.dataInicio),
      horaInicio: formatHora(this.editForm.horaInicio),
      dataFinal: formatDate(this.editForm.dataFinal),
      horaFinal: formatHora(this.editForm.horaFinal),
      observacao: this.editForm.observacao ?? '',
      valorTarifario: this.item.valorTarifario ?? null,
      valorProposta: this.item.valorProposta ?? null,
      valorCompra: this.item.valorCompra ?? null,
      mostrarObsPdf: this.item.mostrarObsPdf ?? 'N',
      kmPrevisto: this.editForm.kmPrevisto ?? 0,
      tempoPrevisto: formatHora('0:00'),
    }

    this.contractItemService.editContractItem(model).subscribe({
      next: (res: any) => {
        if (res?.data) this.item = res.data
        this.snackbar.success('Item atualizado com sucesso!')
        this.response.emit(true)
      },
      error: (err) => {
        this.snackbar.errorRequest(err)
        this.saving = false
      },
      complete: () => { this.saving = false },
    })
  }

  // ── Passageiros ────────────────────────────────────────────────────────────

  removerPassageiro(p: any) {
    const ref = this.dialog.open(DialogConfirmComponent, {
      width: '400px',
      data: {
        title: 'Remover passageiro',
        message: `Deseja remover "${p.nome}" do grupo? O passageiro voltará ao pool.`,
        confirmLabel: 'Remover',
      },
    })
    ref.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return
      this.removingPassageiroId = p.passageiroId
      this.passageiroItemContratoService.delete(p.passageiroId, this.item.id).subscribe({
        next: (res: any) => {
          this.snackbar.success(res?.message || 'Passageiro removido com sucesso!')
          this.loadItem()
        },
        error: () => {
          this.snackbar.error('Não foi possível remover o passageiro.')
          this.removingPassageiroId = null
        },
        complete: () => { this.removingPassageiroId = null },
      })
    })
  }

  esvaziarGrupo() {
    const ref = this.dialog.open(DialogConfirmComponent, {
      width: '400px',
      data: {
        title: 'Esvaziar grupo',
        message: 'Deseja remover todos os passageiros deste grupo? Eles voltarão ao pool.',
        confirmLabel: 'Esvaziar',
      },
    })
    ref.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return
      this.removingAll = true
      this.sugestaoService.removerAssociacoesItem(this.item.id).subscribe({
        next: (res: any) => {
          this.snackbar.success(res?.message || 'Passageiros removidos com sucesso!')
          this.loadItem()
        },
        error: () => {
          this.snackbar.error('Não foi possível esvaziar o grupo.')
          this.removingAll = false
        },
        complete: () => { this.removingAll = false },
      })
    })
  }

  toggleAddPassageiro() {
    this.showAddPassageiro = !this.showAddPassageiro
    if (this.showAddPassageiro) this.loadDisponiveis()
  }

  loadDisponiveis() {
    if (!this.item?.contratoId) return
    this.loadingDisponiveis = true
    this.disponiveis = []
    this.passageiroItemContratoService.getDisponiveis(this.item.contratoId).subscribe({
      next: (res: any) => { this.disponiveis = res.data || [] },
      error: () => { this.loadingDisponiveis = false },
      complete: () => { this.loadingDisponiveis = false },
    })
  }

  adicionarPassageiro(p: any) {
    const passageiroId = p.id ?? p.passageiroId
    this.addingPassageiroId = passageiroId
    this.passageiroItemContratoService.create({ passageiroId, itemContratoId: this.item.id }).subscribe({
      next: () => {
        this.snackbar.success('Passageiro adicionado com sucesso!')
        this.loadItem()
        this.loadDisponiveis()
      },
      error: (err) => {
        this.snackbar.errorRequest(err)
        this.addingPassageiroId = null
      },
      complete: () => { this.addingPassageiroId = null },
    })
  }

  // ── Serviços Adicionais ────────────────────────────────────────────────────

  loadCatalogo() {
    this.serviceAdditionalService.listServiceAdditional().subscribe({
      next: (r: any) => { this.catalogoServicos = r.data || [] },
      error: () => {},
    })
  }

  loadServicos() {
    if (!this.item) return
    this.loadingServicos = true
    this.contratoItemServicoAdicionalService.listPaginated(this.item.id).subscribe({
      next: (r: any) => { this.servicosAdicionais = r.data || [] },
      error: () => { this.loadingServicos = false },
      complete: () => { this.loadingServicos = false },
    })
    this.contratoItemServicoAdicionalService.getValorTotal(this.item.id).subscribe({
      next: (r: any) => { this.valorTotalServicos = r.data?.valorTotal ?? null },
      error: () => {},
    })
  }

  onCatalogoSelect(servicoId: number | null) {
    if (!servicoId) return
    const found = this.catalogoServicos.find(s => s.id === servicoId)
    if (found) {
      this.servicoForm.valorUnitario = found.valorVenda ?? found.valor ?? 0
      this.recalcTotal()
    }
  }

  recalcTotal() {
    const { quantidade, valorUnitario, taxa } = this.servicoForm
    const subtotal = (quantidade || 0) * (valorUnitario || 0)
    const taxaDecimal = (taxa || 0) / 100
    this.servicoForm.valorTotal = parseFloat((subtotal * (1 + taxaDecimal)).toFixed(2))
  }

  addServicoAdicional() {
    const f = this.servicoForm
    if (!f.servicoAdicionalId || !f.dataServico) {
      this.snackbar.error('Preencha o serviço e a data.')
      return
    }
    this.addingServico = true
    this.contratoItemServicoAdicionalService.create({
      contratoItemId: this.item.id,
      dataServico: moment(f.dataServico, 'YYYY-MM-DD').format('DD/MM/YYYY'),
      servicoAdicionalId: f.servicoAdicionalId,
      quantidade: f.quantidade,
      taxa: f.taxa,
      valorUnitario: f.valorUnitario,
      valorTotal: f.valorTotal,
      observacao: f.observacao || null,
    }).subscribe({
      next: () => {
        this.snackbar.success('Serviço adicional adicionado!')
        this.resetServicoForm()
        this.loadServicos()
      },
      error: (err) => {
        this.snackbar.errorRequest(err)
        this.addingServico = false
      },
      complete: () => { this.addingServico = false },
    })
  }

  removeServicoAdicional(servico: any) {
    const ref = this.dialog.open(DialogConfirmComponent, {
      width: '400px',
      data: {
        title: 'Remover serviço',
        message: `Deseja remover "${servico.descricao || 'este serviço'}"?`,
        confirmLabel: 'Remover',
      },
    })
    ref.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return
      this.removingServicoId = servico.id
      this.contratoItemServicoAdicionalService.remove(servico.id).subscribe({
        next: () => {
          this.snackbar.success('Serviço removido!')
          this.loadServicos()
        },
        error: () => {
          this.snackbar.error('Não foi possível remover o serviço.')
          this.removingServicoId = null
        },
        complete: () => { this.removingServicoId = null },
      })
    })
  }

  private resetServicoForm() {
    this.servicoForm = { servicoAdicionalId: null, dataServico: '', quantidade: 1, taxa: 0, valorUnitario: 0, valorTotal: 0, observacao: '' }
  }

  // ── Utilitários ────────────────────────────────────────────────────────────

  compareById(a: any, b: any): boolean {
    if (a == null && b == null) return true
    if (a == null || b == null) return false
    return Number(a) === Number(b)
  }

  private toDateInput(ddmmyyyy: string | null): string {
    if (!ddmmyyyy) return ''
    const parts = ddmmyyyy.split('/')
    return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : ''
  }

  formatCurrency(value: any): string {
    if (value == null) return '-'
    const n = typeof value === 'number' ? value : parseFloat(value)
    if (isNaN(n)) return '-'
    return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
}
